import {IStore, isStoreOperationallyDisabled} from '../../../shared/shared-models/store/store';
import {INavigate} from '../models';
import {NavigationPageEnum, NavigationTabEnum} from '../models/navigations.enums';

/**
 * Dashboard and the stock-manager multi-store tab share one header selection of at most this many stores.
 */
export const SHARED_MULTI_STORE_SELECTION_LIMIT = 5;

/**
 * True when the current page shows the shared multi-store header selection.
 * Home's multi-select stays in the header only. GallixService has no home page, so that
 * page still records last-seen on the single active store.
 */
export function usesSharedMultiStoreLastSeen(navigation: INavigate | null | undefined): boolean {
  if (navigation?.currentPage === NavigationPageEnum.Dashboard) {
    return true;
  }
  return navigation?.currentPage === NavigationPageEnum.StockManager
    && navigation?.currentTab === NavigationTabEnum.StockManagerMultiStore;
}

/**
 * Store ids that should receive the current page on `messages/from_app/last_seen`.
 * Shared multi-store pages write each selected store, capped and ordered the same way as the header.
 * Every other page writes the single active store.
 */
export function getLastSeenStoreIds(
  navigation: INavigate | null | undefined,
  selectedStore: IStore | null | undefined,
  selectedMultipleStores: IStore[] | null | undefined,
  storesInHeaderOrder: IStore[] | null | undefined,
): string[] {
  if (!usesSharedMultiStoreLastSeen(navigation)) {
    return selectedStore?.storeId ? [selectedStore.storeId] : [];
  }

  const selectedIds = new Set<string>(
    (selectedMultipleStores ?? [])
      .filter((store: IStore): boolean => !!store?.storeId && !isStoreOperationallyDisabled(store))
      .map((store: IStore): string => store.storeId),
  );
  if (selectedIds.size === 0) {
    return [];
  }

  const orderedIds: string[] = [];
  for (const store of storesInHeaderOrder ?? []) {
    if (!store?.storeId || isStoreOperationallyDisabled(store) || !selectedIds.has(store.storeId)) {
      continue;
    }
    orderedIds.push(store.storeId);
    if (orderedIds.length >= SHARED_MULTI_STORE_SELECTION_LIMIT) {
      break;
    }
  }
  if (orderedIds.length > 0) {
    return orderedIds;
  }

  const fallbackIds: string[] = [];
  for (const storeId of selectedIds) {
    fallbackIds.push(storeId);
    if (fallbackIds.length >= SHARED_MULTI_STORE_SELECTION_LIMIT) {
      break;
    }
  }
  return fallbackIds;
}
